import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    TextField,
    Button,
    Grid,
    Alert,
    Typography,
    IconButton,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    SelectChangeEvent
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { businessService } from '../../services/businessService';
import { BusinessDetailsDto } from '../../types/business';
import { apiClient } from '../../services/apiClient';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { MuiColorInput } from 'mui-color-input';
import { DEFAULT_THEME, getThemeValue } from '../../constants/themeDefaults';
import { useBusinessTheme } from '../../contexts/BusinessThemeContext';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

interface BusinessDetailsTabProps {
    businessId: string;
}

const BusinessDetailsTab: React.FC<BusinessDetailsTabProps> = ({ businessId }) => {
    const { user } = useAuth();
    const { setTheme } = useBusinessTheme();
    const [businessDetails, setBusinessDetails] = useState<BusinessDetailsDto>({
        id: '',
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        useBusinessHours: false,
        businessHoursStart: null,
        businessHoursEnd: null,
        restrictToBusinessHours: false,
        backgroundColor: DEFAULT_THEME.backgroundColor,
        primaryColor: DEFAULT_THEME.primaryColor,
        secondaryColor: DEFAULT_THEME.secondaryColor,
        navigationColor: DEFAULT_THEME.navigationColor,
        textColor: DEFAULT_THEME.textColor,
        accentColor: DEFAULT_THEME.accentColor,
        firstDayOfWeek: 1,
        subtleAccent: DEFAULT_THEME.subtleAccent,
        defaultCity: '',
        defaultCountry: '',
        defaultAddress: '',
        defaultLatitude: 0,
        defaultLongitude: 0,
        borderColor: DEFAULT_THEME.borderColor
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const isSiteAdmin = user?.isAdmin;
    const isBusinessAdmin = user?.businessRoles?.some(role => 
        role.businessId === businessId && role.isBusinessAdmin
    );
    const canManageImages = isSiteAdmin || isBusinessAdmin;

    const [timeZones] = useState<string[]>([
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Phoenix',
        'America/Anchorage',
        'Pacific/Honolulu',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Moscow',
        'Asia/Dubai',
        'Asia/Shanghai',
        'Asia/Tokyo',
        'Australia/Sydney',
        'Pacific/Auckland'
    ]);

    const loadBusinessDetails = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await businessService.getBusinessDetails(businessId);
            setBusinessDetails(data);
            
            // Update the theme context with the loaded colors
            setTheme({
                backgroundColor: data.backgroundColor || DEFAULT_THEME.backgroundColor,
                primaryColor: data.primaryColor || DEFAULT_THEME.primaryColor,
                secondaryColor: data.secondaryColor || DEFAULT_THEME.secondaryColor,
                navigationColor: data.navigationColor || DEFAULT_THEME.navigationColor,
                textColor: data.textColor || DEFAULT_THEME.textColor,
                accentColor: data.accentColor || DEFAULT_THEME.accentColor,
                subtleAccent: data.subtleAccent || DEFAULT_THEME.subtleAccent,
                borderColor: data.borderColor || DEFAULT_THEME.borderColor
            });
        } catch (err) {
            setError('Failed to load business details. Please try again later.');
            console.error('Error loading business details:', err);
        } finally {
            setIsLoading(false);
        }
    }, [businessId, setTheme]);

    useEffect(() => {
        if (businessId) {
            loadBusinessDetails();
        }
    }, [businessId, loadBusinessDetails]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setBusinessDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            setError(null);
            await businessService.updateBusinessDetails(businessDetails, businessId);
            
            // Update the theme context with the new colors
            setTheme({
                backgroundColor: businessDetails.backgroundColor,
                primaryColor: businessDetails.primaryColor,
                secondaryColor: businessDetails.secondaryColor,
                navigationColor: businessDetails.navigationColor,
                textColor: businessDetails.textColor,
                accentColor: businessDetails.accentColor,
                subtleAccent: businessDetails.subtleAccent,
                borderColor: businessDetails.borderColor
            });
            
            setSuccess('Business details updated successfully');
        } catch (err) {
            setError('Failed to update business details. Please try again later.');
            console.error('Error updating business details:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image size should be less than 5MB');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const formData = new FormData();
            formData.append('image', file);

            // Don't set Content-Type header manually - let axios set it with the boundary
            const response = await apiClient.post(`/images/upload?businessId=${businessId}`, formData);

            if (response.status === 200) {
                setSuccess('Image uploaded successfully');
                // Reload the business details to get the updated image
                await loadBusinessDetails();
            } else {
                throw new Error('Failed to upload image');
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errorMessage || 'Failed to upload image';
            setError(errorMessage);
            console.error('Error uploading image:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageDelete = async () => {
        try {
            setIsLoading(true);
            const response = await apiClient.delete(`/images/${businessId}`);

            if (response.status === 200) {
                setSuccess('Image deleted successfully');
            } else {
                throw new Error('Failed to delete image');
            }
        } catch (error) {
            setError('Failed to delete image');
            console.error('Error deleting image:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTimeZoneChange = (e: SelectChangeEvent) => {
        setBusinessDetails(prev => ({ ...prev, timeZoneId: e.target.value }));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Business Name"
                        name="name"
                        value={businessDetails.name}
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={businessDetails.description}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        value={businessDetails.address}
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        value={businessDetails.phone}
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={businessDetails.email}
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Website"
                        name="website"
                        value={businessDetails.website}
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>Time Zone</InputLabel>
                        <Select
                            value={businessDetails.timeZoneId}
                            onChange={handleTimeZoneChange}
                            label="Time Zone"
                        >
                            {timeZones.map((zone) => (
                                <MenuItem key={zone} value={zone}>
                                    {zone}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={businessDetails.useBusinessHours}
                                onChange={(e) => setBusinessDetails(prev => ({ 
                                    ...prev, 
                                    useBusinessHours: e.target.checked 
                                }))}
                            />
                        }
                        label="Use Business Hours"
                    />
                </Grid>
                {businessDetails.useBusinessHours && (
                    <>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Business Hours Start"
                                type="time"
                                value={businessDetails.businessHoursStart || ''}
                                onChange={(e) => setBusinessDetails(prev => ({ 
                                    ...prev, 
                                    businessHoursStart: e.target.value 
                                }))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300, // 5 min
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Business Hours End"
                                type="time"
                                value={businessDetails.businessHoursEnd || ''}
                                onChange={(e) => setBusinessDetails(prev => ({ 
                                    ...prev, 
                                    businessHoursEnd: e.target.value 
                                }))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    step: 300, // 5 min
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={businessDetails.restrictToBusinessHours}
                                        onChange={(e) => setBusinessDetails(prev => ({ 
                                            ...prev, 
                                            restrictToBusinessHours: e.target.checked 
                                        }))}
                                    />
                                }
                                label="Restrict to Business Hours"
                            />
                        </Grid>
                    </>
                )}
                {canManageImages && (
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Business Image</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {businessId && (
                                <Box
                                    component="img"
                                    src={`/api/images/${businessId}`}
                                    alt="Business background"
                                    sx={{
                                        width: '100%',
                                        maxWidth: 400,
                                        height: 200,
                                        objectFit: 'contain',
                                        borderRadius: 1,
                                        mb: 2,
                                        bgcolor: 'grey.100'
                                    }}
                                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            )}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Button
                                    component="label"
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    disabled={isLoading}
                                >
                                    Upload Image
                                    <VisuallyHiddenInput
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                    />
                                </Button>
                                <IconButton
                                    onClick={handleImageDelete}
                                    disabled={isLoading}
                                    color="error"
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Upload an image to be displayed as the calendar background (max 5MB)
                        </Typography>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Theme Customization</Typography>
                    <Grid container spacing={2} sx={{ mt: 3 }}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>Background Color</Typography>
                            <MuiColorInput
                                format="hex"
                                value={getThemeValue(businessDetails.backgroundColor, 'backgroundColor')}
                                onChange={(color: string) => setBusinessDetails(prev => ({
                                    ...prev,
                                    backgroundColor: color
                                }))}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Used for the main background
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>Primary Color</Typography>
                            <MuiColorInput
                                format="hex"
                                value={getThemeValue(businessDetails.primaryColor, 'primaryColor')}
                                onChange={(color: string) => setBusinessDetails(prev => ({
                                    ...prev,
                                    primaryColor: color
                                }))}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Used for important elements, highlights, and calls to action
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>Text Color</Typography>
                            <MuiColorInput
                                format="hex"
                                value={getThemeValue(businessDetails.textColor, 'textColor')}
                                onChange={(color: string) => setBusinessDetails(prev => ({
                                    ...prev,
                                    textColor: color
                                }))}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Used for text, icons, and borders
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>Navigation Color</Typography>
                            <MuiColorInput
                                format="hex"
                                value={getThemeValue(businessDetails.navigationColor, 'navigationColor')}
                                onChange={(color: string) => setBusinessDetails(prev => ({
                                    ...prev,
                                    navigationColor: color
                                }))}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Used for navigation bars, menus, and main UI structure
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>Secondary Color</Typography>
                            <MuiColorInput
                                format="hex"
                                value={getThemeValue(businessDetails.secondaryColor, 'secondaryColor')}
                                onChange={(color: string) => setBusinessDetails(prev => ({
                                    ...prev,
                                    secondaryColor: color
                                }))}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Used for navigation text, secondary actions, highlights, and complementary elements
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>Accent Color</Typography>
                            <MuiColorInput
                                format="hex"
                                value={getThemeValue(businessDetails.accentColor, 'accentColor')}
                                onChange={(color: string) => setBusinessDetails(prev => ({
                                    ...prev,
                                    accentColor: color
                                }))}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Used for accents, highlights, and interactive elements
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            These colors will be applied to your business's interface throughout the application.
                            Changes will affect all users associated with this business.
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>First Day of Week</InputLabel>
                        <Select
                            value={businessDetails.firstDayOfWeek}
                            onChange={(e) => setBusinessDetails(prev => ({ ...prev, firstDayOfWeek: Number(e.target.value) }))}
                            label="First Day of Week"
                        >
                            <MenuItem value={1}>Monday</MenuItem>
                            <MenuItem value={2}>Tuesday</MenuItem>
                            <MenuItem value={3}>Wednesday</MenuItem>
                            <MenuItem value={4}>Thursday</MenuItem>
                            <MenuItem value={5}>Friday</MenuItem>
                            <MenuItem value={6}>Saturday</MenuItem>
                            <MenuItem value={0}>Sunday</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        fullWidth
                        sx={{ mt: 3 }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BusinessDetailsTab; 